import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_OAUTH_URL = "https://demobackend.emergentagent.com/auth/v1/env/oauth/google/authorize";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('subspace_token'));
  const [loading, setLoading] = useState(true);

  // Set axios auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Handle Google OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId && !token) {
        try {
          const response = await axios.post(`${API}/auth/google/session`, {
            session_id: sessionId
          });
          
          const { token: newToken, user: userData } = response.data;
          
          localStorage.setItem('subspace_token', newToken);
          setToken(newToken);
          setUser(userData);
          
          // Clear URL params
          window.history.replaceState({}, document.title, window.location.pathname);
          
          toast.success(`Welcome, ${userData.name}!`);
        } catch (error) {
          console.error('Google OAuth failed:', error);
          toast.error('Google sign in failed');
        }
      }
    };
    
    handleOAuthCallback();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('subspace_token', newToken);
      setToken(newToken);
      setUser(userData);
      toast.success(`Welcome back, ${userData.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('subspace_token', newToken);
      setToken(newToken);
      setUser(userData);
      toast.success(`Welcome to Subspace, ${userData.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const loginWithGoogle = useCallback(() => {
    // Redirect to Google OAuth
    const redirectUri = encodeURIComponent(window.location.origin);
    window.location.href = `${GOOGLE_OAUTH_URL}?redirect_uri=${redirectUri}`;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('subspace_token');
    setToken(null);
    setUser(null);
    toast.info('Logged out');
  }, []);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Refresh failed:', error);
      }
    }
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isPremium: user?.is_premium || user?.is_admin,
    isAdmin: user?.is_admin,
    login,
    register,
    loginWithGoogle,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
